import {ComplianceManagerApi} from "../../../compliance-manager";
import {Button, Col, Descriptions, Row, Tabs, Typography} from "antd";
import ComplianceStatusButton from "components/compliance/compliance-status-button";
import './veriflyt-tab.style.less';
import {momentOrNull} from "common/util";
import useBreakpoints from "hooks/useBreakpoints";
import {ProviderType, VeriflytResultFragmentFragment} from "__generated__/graphql/types";
import StatusTable from "./status-table";
import {usePaxNodeQuery} from "../../../../graphql";
import React from "react";
import RegisterVeriflytAccount from "./register-veriflyt-account";
import VeriflytTicketEditor from "../../../../../editor/veriflyt-ticket-editor";
import MCIcon from "../../../../../icon";
import {useHistory} from "react-router-dom";
import useUserGroups from "../../../../../../hooks/useUserGroups";
import NotAuthorized from "../../../../../NotAuthorized";
import {NextExpirationDate} from "../../../../NextExpirationDate";

/**
 * Tab for displaying Compliance (FlytComply) result details.
 */
export default function VeriflytTab(props: { api: ComplianceManagerApi }){

    const { api } = props;

    const bp = useBreakpoints();
    const history = useHistory();
    const [ userGroups ] = useUserGroups();

    const canListVeriflytRecords = userGroups.includes('flytsuite.compliance.ticket.all');

    const isSmallScreen = bp.max("medium");

    const complianceResult = api.complianceResult;
    const normalizedVeriflytResult = complianceResult?.veriflytProviderResult;
    const veriflytResult = normalizedVeriflytResult?._veriflytResult;

    const failingDetails: VeriflytResultFragmentFragment['failingDetails'] = veriflytResult?.failingDetails || [];
    const passingDetails: VeriflytResultFragmentFragment['passingDetails'] = veriflytResult?.passingDetails || [];
    const hasFailures = failingDetails.length > 0;
    const hasPasses = passingDetails.length > 0;
    const hasError = veriflytResult?.errorMessage && veriflytResult?.errorMessage.length > 0;

    const header = (
        <Row style={{marginTop: "1.5rem", marginBottom: "1rem"}} type="flex" gutter={12}>
            {api.paxContext?.lastName ||api.paxContext?.firstName ? (
                <Col>
                    <h4>VERIFLYT Details for <span style={{textTransform: 'capitalize'}}>{api.paxContext?.lastName}</span>, <span style={{textTransform: 'capitalize'}} >{api.paxContext?.firstName}</span></h4>
                </Col>
            ) : null}
        </Row>
    )

    const details = (
        <Descriptions
            className="mc-compliance-manager-details"
            bordered
            column={1}
            size="small"
            layout={isSmallScreen ? 'vertical' : 'horizontal'}
        >
            <Descriptions.Item label="Status">
                <ComplianceStatusButton
                    pax={api.paxContext}
                    disabled
                    showSourcesTooltip={false}
                    selectedProvider={ProviderType.VERIFLYT}
                />
            </Descriptions.Item>
            <Descriptions.Item label="VERIFLYT ID">
                {veriflytResult?.personID}
            </Descriptions.Item>
            <Descriptions.Item label="Reason">
                {normalizedVeriflytResult?.reason}
            </Descriptions.Item>
            <Descriptions.Item label="Last Check Time">
                {momentOrNull(normalizedVeriflytResult?.startTs)?.format('YYYY-MM-DD HH:MM')}
            </Descriptions.Item>
            {!!normalizedVeriflytResult?.nextExpirationDate && (
                <Descriptions.Item label="Next Expiration">
                    <NextExpirationDate date={normalizedVeriflytResult.nextExpirationDate} />
                    {' '}
                    {normalizedVeriflytResult?.nextExpirationQualificationName ? `(${normalizedVeriflytResult?.nextExpirationQualificationName})` : ''}
                </Descriptions.Item>
            )}
            {hasError ? (
                <Descriptions.Item label="Error Message" className="mc-compliance-manager-error">
                    <Typography.Text type="danger">{veriflytResult?.errorMessage}</Typography.Text>
                </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Duration">
                {normalizedVeriflytResult?.latencyMs} ms
            </Descriptions.Item>
        </Descriptions>
    )

    const complianceInfo = (
        <>
            {hasFailures ? (
                <Descriptions bordered layout="vertical" style={{ marginTop: 12 }} size="small">
                    <Descriptions.Item
                        label={"Reasons for non-compliance (" + failingDetails.length + ")"}
                        className="mc-compliance-manager-reasons">
                        <div className="mc-compliance-manager-reasons-scroll">
                            <StatusTable statuses={failingDetails} />
                        </div>
                    </Descriptions.Item>
                </Descriptions>
            ) : null}
            {hasPasses ? (
                <Descriptions bordered layout="vertical" style={{ marginTop: 12 }} size="small">
                    <Descriptions.Item
                        label={"Satisfied requirements " + "(" + passingDetails.length + ")"}
                        className="mc-compliance-manager-reasons">
                        <div className="mc-compliance-manager-reasons-scroll">
                            <StatusTable statuses={passingDetails} />
                        </div>
                    </Descriptions.Item>
                </Descriptions>
            ) : null}
        </>
    )

    const href = `/app/veriflyt/records?personID=${api.paxContext.personID}`;

    const tabs = (
        <Tabs className="mc-compliance-manager-veriflyt-tabs" defaultActiveKey="info">
            <Tabs.TabPane tab="Compliance Info" key="info">
                {complianceInfo}
            </Tabs.TabPane>
            <Tabs.TabPane tab="Certifications" key="certs" style={{ position: 'relative' }}>
                {canListVeriflytRecords ? (
                    <>
                        <div style={{ position: 'absolute', top: 6, right: 0 }}>
                            <a
                                className="ant-btn ant-btn-primary"
                                href={href}
                                target="_blank"
                                rel="noreferrer noopener"
                                style={{ zIndex: 1 }}
                            >
                                Edit <MCIcon type="external-link" style={{ marginLeft: 6 }} />
                            </a>
                        </div>
                        <VeriflytTicketEditor personID={api.paxContext.personID} readOnly compact />
                    </>
                ) : (
                    <NotAuthorized
                        desciption="You are not authorized to view Veriflyt certification records."
                    />
                )}
            </Tabs.TabPane>
        </Tabs>
    )

    return <div style={{ marginTop: 12 }}>
        {header}
        {details}
        {tabs}

        <RegisterVeriflytAccount api={api} />
    </div>
}